import { useCallback, useEffect, useRef, useState } from 'react'
import { Functions } from 'firebase/functions'
import { api } from '../../../lib/api'
import {
  CLEO_CALLABLE_TIMEOUT_MS,
  CLEO_STORED_POLL_BUDGET_MS,
  CLEO_STORED_POLL_INTERVAL_MS,
} from '../../../lib/shared/cleoSuggestionTiming'

// The operator-facing status of the Cleo reply draft for the message currently
// awaiting a reply.
//   idle       — nothing to show; the composer offers "Generate suggestion"
//   generating — a draft is being produced (background poll or on-demand run)
//   ready      — a draft is available for the operator to act on
//   noReply    — Cleo deliberately declined (the thread needs no answer)
//   error      — a generation attempt failed
export type CleoSuggestionStatus =
  | 'idle'
  | 'generating'
  | 'ready'
  | 'noReply'
  | 'error'

// What prompted a Cleo run — forwarded to the backend for tracking. The inbox
// no longer drafts on its own, so both remaining sources are explicit advisor
// actions. The backend still accepts 'auto_fallback' so a tab left open across
// a deploy is not rejected.
type CleoGenerateSource = 'on_demand' | 'regenerate'

interface CleoSuggestResponse {
  suggestion?: string | null
  suggestionId?: string | null
  // True when Cleo deliberately declined to draft a reply (the conversation
  // needs no answer), as opposed to a genuine failure.
  noReplyNeeded?: boolean
}

// chatwoot/get_cleo_suggestion — the background suggestion stored when the
// customer message arrived.
interface StoredCleoSuggestionResponse {
  status: 'none' | 'pending' | 'generating' | 'ready' | 'failed' | 'no_reply'
  suggestion?: string | null
  suggestionId?: string | null
  forMessageId?: string | null
  // The draft the operator already copied or dismissed. When it matches the
  // stored draft, this conversation is settled: don't re-show it and don't
  // generate a replacement.
  consumedSuggestionId?: string | null
}

// While the background draft is still generating, re-read this often until it's
// ready (or we give up and fall back to on-demand generation).
const STORED_POLL_INTERVAL_MS = CLEO_STORED_POLL_INTERVAL_MS
// Outlive the server's own staleness verdict so it decides when a run is dead
// rather than us abandoning a healthy-but-slow one.
const STORED_POLL_BUDGET_MS = CLEO_STORED_POLL_BUDGET_MS
const STORED_POLL_MAX_ATTEMPTS = Math.ceil(
  STORED_POLL_BUDGET_MS / STORED_POLL_INTERVAL_MS
)
const STORED_NONE_GRACE_ATTEMPTS = 1

interface UseChatwootCleoSuggestionArgs {
  functions: Functions
  conversationId: string
  companyUid?: string | null
  // Id of the newest incoming message still without a reply, or null when
  // nothing awaits an answer. When null the hook stays idle: Cleo drafts only
  // for unanswered customer messages (Intercom parity).
  awaitingReplyMessageId: string | null
}

export interface UseChatwootCleoSuggestion {
  status: CleoSuggestionStatus
  suggestion: string | null
  suggestionId: string | null
  // On-demand generation (the composer's "Generate suggestion" button).
  generate: () => Promise<void>
  // Rework the current draft, optionally with extra instructions.
  regenerate: (instructions?: string) => Promise<void>
  // Drop the current draft and return to idle (reject / after copy / after send).
  reset: () => void
}

/**
 * Owns the Cleo reply-draft lifecycle for the awaited message: reads
 * the background suggestion produced when the customer message arrived (polling
 * while it generates), and drives on-demand generation and regeneration. Purely
 * a data hook — the composer decides how to render each status.
 *
 * State is scoped to (conversationId, awaitingReplyMessageId): when either
 * changes — a conversation switch or a fresh customer message — it re-reads the
 * background draft for the new context, so a stale suggestion never carries over.
 */
export function useChatwootCleoSuggestion({
  functions,
  conversationId,
  companyUid,
  awaitingReplyMessageId,
}: UseChatwootCleoSuggestionArgs): UseChatwootCleoSuggestion {
  const [status, setStatus] = useState<CleoSuggestionStatus>('idle')
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const [suggestionId, setSuggestionId] = useState<string | null>(null)
  const suggestionRef = useRef(suggestion)
  suggestionRef.current = suggestion
  const suggestionIdRef = useRef(suggestionId)
  suggestionIdRef.current = suggestionId

  // The message the current state belongs to, read from async callbacks so a
  // late response for a since-superseded message can't overwrite fresh state.
  const awaitedRef = useRef(awaitingReplyMessageId)
  awaitedRef.current = awaitingReplyMessageId
  const conversationRef = useRef(conversationId)
  conversationRef.current = conversationId
  const companyRef = useRef(companyUid)
  companyRef.current = companyUid

  // Identifies the (conversation, company, awaited message) a draft belongs to.
  // React object identity (`functions`, `run`) churns on unrelated parent
  // renders, so comparing this tells a real context change from mere churn.
  const contextKey = `${conversationId}\u0000${companyUid ?? ''}\u0000${awaitingReplyMessageId ?? ''}`
  const contextKeyRef = useRef(contextKey)
  contextKeyRef.current = contextKey

  const run = useCallback(
    async (source: CleoGenerateSource, instructions?: string) => {
      const requestedFor = awaitedRef.current
      const isStale = () =>
        awaitedRef.current !== requestedFor ||
        conversationRef.current !== conversationId ||
        companyRef.current !== companyUid
      const startedFor = contextKeyRef.current
      // Explicitly asking for a draft reopens a settled context.
      if (settledForRef.current === startedFor) {
        settledForRef.current = null
      }
      setStatus('generating')
      try {
        const response = (await api(
          functions,
          'chatwoot/cleo_suggest',
          {
            conversationId,
            companyUid: companyUid || undefined,
            source,
            ...(requestedFor ? { forMessageId: requestedFor } : {}),
            ...(source === 'regenerate' && instructions
              ? { regenerationInstructions: instructions }
              : {}),
            ...(source === 'regenerate' && suggestionRef.current
              ? { previousSuggestion: suggestionRef.current }
              : {}),
            ...(source === 'regenerate' && suggestionIdRef.current
              ? { parentSuggestionId: suggestionIdRef.current }
              : {}),
          },
          { timeout: CLEO_CALLABLE_TIMEOUT_MS }
        )) as CleoSuggestResponse
        if (isStale()) {
          return
        }
        const text = response?.suggestion ?? ''
        if (!text) {
          setSuggestion(null)
          setSuggestionId(null)
          setStatus(response?.noReplyNeeded ? 'noReply' : 'error')
          return
        }
        setSuggestion(text)
        setSuggestionId(response.suggestionId ?? null)
        setStatus('ready')
      } catch (_error) {
        if (isStale()) {
          return
        }
        setSuggestion(null)
        setSuggestionId(null)
        setStatus('error')
      }
    },
    [functions, conversationId, companyUid]
  )

  const shownForRef = useRef<string | null>(null)
  // Contexts that need no automatic draft: she copied or dismissed the reply, so
  // neither a re-read nor a failed read may spend a run here.
  const settledForRef = useRef<string | null>(null)

  // Read the draft the backend generated when the customer message arrived, and
  // poll while it is still working. The backend is the only producer.
  // A per-effect `cancelled` flag gates state updates and the next poll, so a
  // response resolving after the awaited message changed can't set stale state.
  useEffect(() => {
    // Only a genuine context change may discard the draft on screen; on churn,
    // re-read without blanking what the operator is working with.
    if (shownForRef.current !== contextKey) {
      shownForRef.current = contextKey
      setSuggestion(null)
      setSuggestionId(null)
      if (!awaitingReplyMessageId) {
        setStatus('idle')
        return
      }
      setStatus('generating')
    } else if (
      !awaitingReplyMessageId ||
      settledForRef.current === contextKey
    ) {
      // She dropped this draft; re-reading would only bring it back.
      return
    }

    let cancelled = false
    let timer: number | null = null

    function pollAgain(attempt: number): void {
      timer = window.setTimeout(
        () => void loadStoredSuggestion(attempt),
        STORED_POLL_INTERVAL_MS
      )
    }

    async function loadStoredSuggestion(attempt: number): Promise<void> {
      try {
        const response = (await api(functions, 'chatwoot/get_cleo_suggestion', {
          conversationId,
          companyUid: companyUid || undefined,
        })) as StoredCleoSuggestionResponse
        if (cancelled) {
          return
        }
        // A stored suggestion tied to a different (older) message doesn't apply.
        const matches =
          !response.forMessageId ||
          response.forMessageId === awaitingReplyMessageId

        // The operator already copied or dismissed this draft, so the thread is
        // settled: stay idle rather than re-showing it or spending a run on a
        // replacement. The composer still offers explicit generation.
        const settled =
          !!response.consumedSuggestionId &&
          response.consumedSuggestionId === response.suggestionId
        if (matches && settled) {
          settledForRef.current = contextKey
          setStatus('idle')
          return
        }

        if (matches && response.status === 'ready' && response.suggestion) {
          setSuggestion(response.suggestion)
          setSuggestionId(response.suggestionId ?? null)
          setStatus('ready')
          return
        }
        if (matches && response.status === 'no_reply') {
          setStatus('noReply')
          return
        }
        if (
          matches &&
          (response.status === 'pending' || response.status === 'generating')
        ) {
          if (attempt < STORED_POLL_MAX_ATTEMPTS) {
            pollAgain(attempt + 1)
          } else {
            setStatus('error')
          }
          return
        }
        if (
          matches &&
          response.status === 'none' &&
          attempt < STORED_NONE_GRACE_ATTEMPTS
        ) {
          pollAgain(attempt + 1)
          return
        }
        // Nothing usable, and the backend is not about to produce one. A draft
        // the inbox generated here would be a second run for a message the
        // webhook already owns, so offer generation instead of spending one.
        // A backend failure stays visible as an error.
        setStatus(matches && response.status === 'failed' ? 'error' : 'idle')
      } catch (_error) {
        if (cancelled) {
          return
        }
        // A failed read says nothing about whether a draft is needed, so it must
        // never cost her the draft on screen — keep it and retry. With nothing on
        // screen there is no honest "coming" to show, so offer generation rather
        // than spend a run on a guess.
        if (suggestionRef.current) {
          if (attempt < STORED_POLL_MAX_ATTEMPTS) {
            pollAgain(attempt + 1)
          }
          return
        }
        setStatus('idle')
      }
    }

    void loadStoredSuggestion(0)
    return () => {
      cancelled = true
      if (timer !== null) {
        window.clearTimeout(timer)
      }
    }
  }, [
    functions,
    conversationId,
    companyUid,
    awaitingReplyMessageId,
    contextKey,
  ])

  const generate = useCallback(() => run('on_demand'), [run])
  const regenerate = useCallback(
    (instructions?: string) => run('regenerate', instructions),
    [run]
  )
  const reset = useCallback(() => {
    // Dismissing is recorded server-side as consumed, but until that write lands
    // a re-read would resurrect the draft she just dropped, so remember it here.
    settledForRef.current = contextKeyRef.current
    setSuggestion(null)
    setSuggestionId(null)
    setStatus('idle')
  }, [])

  return { status, suggestion, suggestionId, generate, regenerate, reset }
}
